import { useCallback, useState } from 'react';

import {
  api,
  type ClientRow,
  type ContractorRow,
  type RecruiterRow,
  type Summary,
  type TierInfo,
  type TrendPoint,
  type WorkforceComposition,
} from './api';
import { BarList, BarListSummary, RevenueArea } from './components/Charts';
import {
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import {
  KpiCard,
  MarginBadge,
  MiniBarChart,
  MiniDonut,
  StackedBar,
  computeTrend,
} from './components/KpiCard';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import {
  computeCommission,
  hours,
  marginPercent,
  money,
  moneyCompact,
  rate,
  shortDate,
} from './format';

import { Shell, type ShellProps } from './Shell';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';

interface Data {
  readonly summary: Summary;
  readonly trend: readonly TrendPoint[];
  readonly clients: readonly ClientRow[];
  readonly contractors: readonly ContractorRow[];
  readonly recruiters: readonly RecruiterRow[];
  readonly tiers: readonly TierInfo[];
  readonly workforce: WorkforceComposition;
}

function concentrationPct(top: number, total: number): string {
  if (total === 0) return '—';
  return `${((top / total) * 100).toFixed(1)}%`;
}

type ExecTab = 'overview' | 'gp-detail' | 'commission';

export function Executive(props: ShellProps) {
  const { session, navigate } = props;
  const [search, setSearch] = useState('');
  const tf = useTimeFilter();
  const [tab, setTab] = useState<ExecTab>('overview');

  const canSeeCommission = session.permissions.includes('commission:read:all');

  const { data, error } = useAsyncData<Data>(
    () => {
      const dateRange = tf.toDateRange();
      return Promise.all([
        api.summary(dateRange),
        api.trend(dateRange),
        api.clients(dateRange),
        api.contractors(dateRange),
        canSeeCommission ? api.recruiters(dateRange) : Promise.resolve([]),
        canSeeCommission ? api.tiers() : Promise.resolve([]),
        api.workforceComposition(),
      ]).then(([summary, trend, clients, contractors, recruiters, tiers, workforce]) => ({
        summary,
        trend,
        clients,
        contractors,
        recruiters,
        tiers,
        workforce,
      }));
    },
    [canSeeCommission, tf.selection],
    'Could not load the workspace.',
  );

  const activeContractors =
    data !== null ? data.contractors.filter((c) => Number(c.weeks) > 0) : [];

  const filteredContractors = activeContractors.filter(
    (r) =>
      search === '' ||
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.clientName.toLowerCase().includes(search.toLowerCase()),
  );

  const accessor = useCallback((row: ContractorRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      client: row.clientName,
      type: row.workerType,
      hours: row.hours,
      gpPerHour: row.grossProfitPerHour,
      grossProfit: row.grossProfit,
      margin: marginPercent(row.grossProfit, row.billed),
    };
    return map[key];
  }, []);

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filteredContractors, accessor);

  if (error !== null) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props}>
        <Card>
          <Spinner label="Loading executive dashboard…" />
        </Card>
      </Shell>
    );
  }

  const { summary, trend, clients, recruiters, tiers, workforce } = data;

  const avgGpPerHour =
    summary.hours !== '0'
      ? rate(String((Number(summary.grossProfit) / Number(summary.hours)).toFixed(4)))
      : null;

  const top5Clients = clients.filter((c) => c.grossProfit !== '0').slice(0, 5);

  const topConcentration =
    top5Clients.length > 0
      ? concentrationPct(
          top5Clients.reduce((sum, c) => sum + Number(c.grossProfit), 0),
          Number(summary.grossProfit),
        )
      : '—';

  const mid = Math.floor(trend.length / 2);
  const priorTrend = trend.slice(0, mid);
  const currentTrend = trend.slice(mid);

  const billedSparkline = trend.map((p) => Number(p.billed));
  const gpSparkline = trend.map((p) => Number(p.grossProfit));
  const marginSparkline = trend.map((p) =>
    Number(p.billed) > 0 ? (Number(p.grossProfit) / Number(p.billed)) * 100 : 0,
  );
  const gpPerHourSparkline = trend.map((p) =>
    Number(p.hours) > 0 ? Number(p.grossProfit) / Number(p.hours) : 0,
  );
  const headcountSparkline = trend.map((p) => p.contractors);

  const priorRevenue = priorTrend.reduce((s, p) => s + Number(p.billed), 0);
  const currentRevenue = currentTrend.reduce((s, p) => s + Number(p.billed), 0);
  const priorGp = priorTrend.reduce((s, p) => s + Number(p.grossProfit), 0);
  const currentGp = currentTrend.reduce((s, p) => s + Number(p.grossProfit), 0);
  const priorHours = priorTrend.reduce((s, p) => s + Number(p.hours), 0);
  const currentHours = currentTrend.reduce((s, p) => s + Number(p.hours), 0);
  const priorHeadcount = priorTrend.length > 0 ? priorTrend[priorTrend.length - 1]!.contractors : 0;
  const currentHeadcount =
    currentTrend.length > 0 ? currentTrend[currentTrend.length - 1]!.contractors : 0;

  const margin =
    Number(summary.billed) > 0 ? (Number(summary.grossProfit) / Number(summary.billed)) * 100 : 0;

  const priorMargin = priorRevenue > 0 ? (priorGp / priorRevenue) * 100 : 0;
  const currentMargin = currentRevenue > 0 ? (currentGp / currentRevenue) * 100 : 0;

  const priorGpPerHour = priorHours > 0 ? priorGp / priorHours : 0;
  const currentGpPerHour = currentHours > 0 ? currentGp / currentHours : 0;

  const commissionByRecruiter = recruiters.map((r) => ({
    name: r.name,
    id: r.id,
    grossProfit: r.grossProfit,
    hours: r.hours,
    contractors: r.contractors,
    commission: computeCommission(tiers, r.grossProfit),
  }));

  const totalLoadedCost = Number(summary.billed) - Number(summary.grossProfit);
  const totalCommission = commissionByRecruiter
    .reduce((sum, r) => sum + Number(r.commission), 0)
    .toFixed(4);

  const top5Bars = top5Clients.map((c) => ({
    label: c.name,
    value: Number(c.grossProfit),
  }));

  return (
    <Shell {...props}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Executive Dashboard</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Company-wide financial performance and headcount
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      {/* ── KPI strip (always visible) ── */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Revenue"
          value={moneyCompact(summary.billed)}
          exact={money(summary.billed)}
          tone="revenue"
          delay={0}
          trend={computeTrend(currentRevenue, priorRevenue)}
          trendContext="vs prior period"
          sparkline={billedSparkline}
          sparklineColor="#2563eb"
          sparklineLabel="rev/wk"
        />
        <KpiCard
          label="Gross Profit"
          value={moneyCompact(summary.grossProfit)}
          exact={money(summary.grossProfit)}
          tone="profit"
          delay={40}
          trend={computeTrend(currentGp, priorGp)}
          trendContext="vs prior period"
          sparkline={gpSparkline}
          sparklineColor="#16a34a"
          sparklineLabel="GP/wk"
        >
          <MarginBadge margin={margin} />
        </KpiCard>
        <KpiCard
          label="GP Margin"
          value={marginPercent(summary.grossProfit, summary.billed)}
          tone="profit"
          delay={80}
          trend={computeTrend(currentMargin, priorMargin)}
          trendContext="vs prior period"
          sparkline={marginSparkline}
          sparklineColor="#16a34a"
          sparklineLabel="margin %"
        />
        <KpiCard
          label="Avg GP/Hour"
          value={avgGpPerHour ?? '—'}
          delay={120}
          trend={computeTrend(currentGpPerHour, priorGpPerHour)}
          trendContext="vs prior period"
          sparkline={gpPerHourSparkline}
          sparklineColor="#1c1c1a"
          sparklineLabel="$/hr"
        />
        <KpiCard
          label="Headcount"
          value={String(summary.activeHeadcount)}
          subtitle={`${String(workforce.w2)} W2 / ${String(workforce.contractor1099)} 1099`}
          delay={160}
          trend={computeTrend(currentHeadcount, priorHeadcount)}
          trendContext="vs prior period"
          sparkline={headcountSparkline}
          sparklineColor="#16a34a"
          sparklineLabel="placements"
        >
          <StackedBar
            segments={[
              { value: workforce.w2, color: '#16a34a', label: 'W2' },
              { value: workforce.contractor1099, color: '#2563eb', label: '1099' },
            ]}
          />
        </KpiCard>
        <KpiCard
          label="Top-5 Concentration"
          value={topConcentration}
          subtitle={top5Clients.length > 0 ? top5Clients[0]!.name : ''}
          tone={
            topConcentration !== '—' && parseFloat(topConcentration) > 60 ? 'caution' : 'neutral'
          }
          delay={200}
          footer={
            <MiniBarChart
              bars={top5Bars}
              height={56}
              formatValue={(v) => moneyCompact(v.toFixed(2))}
            />
          }
        />
      </div>

      {/* ── Tab bar ── */}
      <TabBar active={tab} onChange={setTab} canSeeCommission={canSeeCommission} />

      {/* ── Tab content ── */}
      {tab === 'overview' && (
        <OverviewTab
          summary={summary}
          trend={trend}
          activeContractors={activeContractors}
          workforce={workforce}
          totalLoadedCost={totalLoadedCost}
        />
      )}

      {tab === 'gp-detail' && (
        <GpDetailTab
          summary={summary}
          clients={clients}
          top5Clients={top5Clients}
          topConcentration={topConcentration}
          activeContractors={activeContractors}
          filteredContractors={filteredContractors}
          sorted={sorted}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={onSort}
          search={search}
          setSearch={setSearch}
          navigate={navigate}
          avgGpPerHour={avgGpPerHour}
        />
      )}

      {tab === 'commission' && canSeeCommission && (
        <CommissionTab
          commissionByRecruiter={commissionByRecruiter}
          totalCommission={totalCommission}
          tiers={tiers}
          navigate={navigate}
        />
      )}
    </Shell>
  );
}

/* ── Tab bar ── */

function TabBar({
  active,
  onChange,
  canSeeCommission,
}: {
  active: ExecTab;
  onChange: (t: ExecTab) => void;
  canSeeCommission: boolean;
}) {
  const tabs: { id: ExecTab; label: string; needsCommission?: boolean }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'gp-detail', label: 'GP Detail' },
    { id: 'commission', label: 'Commission', needsCommission: true },
  ];
  return (
    <div className="mt-4 flex gap-1 border-b border-edge">
      {tabs
        .filter((t) => !t.needsCommission || canSeeCommission)
        .map((t) => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
              active === t.id
                ? 'border-profit text-profit'
                : 'border-transparent text-ink-muted hover:text-ink hover:border-edge'
            }`}
          >
            {t.label}
          </button>
        ))}
    </div>
  );
}

/* ── Overview Tab ── */

function OverviewTab({
  summary,
  trend,
  activeContractors,
  workforce,
  totalLoadedCost,
}: {
  summary: Summary;
  trend: readonly TrendPoint[];
  activeContractors: readonly ContractorRow[];
  workforce: WorkforceComposition;
  totalLoadedCost: number;
}) {
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Card delay={240}>
        <CardHeader
          title="Revenue and gross profit trend"
          subtitle="Weekly. The gap between the lines is loaded cost plus expenses."
        />
        <RevenueArea
          compact
          points={trend.map((p) => ({
            label: p.weekEnding,
            billed: p.billed,
            grossProfit: p.grossProfit,
          }))}
        />
      </Card>

      <Card delay={260}>
        <CardHeader title="Contractor mix" />
        <div className="space-y-4 px-5 py-4">
          <MixStat
            label="W2 employees"
            count={activeContractors.filter((c) => c.workerType !== 'contractor_1099').length}
            total={activeContractors.length}
            color="bg-revenue"
          />
          <MixStat
            label="1099 contractors"
            count={activeContractors.filter((c) => c.workerType === 'contractor_1099').length}
            total={activeContractors.length}
            color="bg-profit"
          />
        </div>

        <div className="border-t border-edge px-5 py-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Period
          </p>
          <p className="mt-1 text-sm text-ink">
            {summary.firstWeek !== null && summary.lastWeek !== null
              ? `${shortDate(summary.firstWeek)} – ${shortDate(summary.lastWeek)}`
              : '—'}
          </p>
          <p className="mt-0.5 text-xs text-ink-faint">
            {summary.weeks.toLocaleString()} contractor-weeks across {summary.clients} customers
          </p>
        </div>

        <div className="border-t border-edge px-5 py-4">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
            Billable hours
          </p>
          <p className="figure mt-1 text-xl font-semibold text-ink">{hours(summary.hours)}</p>
        </div>
      </Card>

      <Card delay={440}>
        <CardHeader
          title="Company financials bridge"
          subtitle="Revenue to gross profit waterfall"
        />
        <div className="space-y-3 px-5 py-4">
          <BridgeRow label="Revenue (total billed)" value={money(summary.billed)} tone="revenue" />
          <BridgeRow
            label="Less: Loaded cost + expenses"
            value={`(${money(totalLoadedCost.toFixed(2))})`}
            tone="muted"
            indent
          />
          <div className="border-t border-edge pt-3">
            <BridgeRow label="Gross Profit" value={money(summary.grossProfit)} tone="profit" bold />
          </div>
          <div className="pt-1">
            <BridgeRow
              label="GP margin"
              value={marginPercent(summary.grossProfit, summary.billed)}
              tone="profit"
            />
          </div>
        </div>
      </Card>

      <Card delay={460}>
        <CardHeader title="This period at a glance" subtitle="Key operating metrics" />
        <div className="space-y-3 px-5 py-4">
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Active contractors</span>
            <span className="figure font-medium text-ink">{activeContractors.length}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">W2 / 1099 split</span>
            <span className="figure font-medium text-ink">
              {workforce.w2} / {workforce.contractor1099}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">Total loaded cost</span>
            <span className="figure font-medium text-ink">{money(totalLoadedCost.toFixed(2))}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ── GP Detail Tab ── */

function GpDetailTab({
  summary,
  clients,
  top5Clients,
  topConcentration,
  activeContractors,
  filteredContractors: _filteredContractors,
  sorted,
  sortKey,
  sortDirection,
  onSort,
  search,
  setSearch,
  navigate,
  avgGpPerHour,
}: {
  summary: Summary;
  clients: readonly ClientRow[];
  top5Clients: readonly ClientRow[];
  topConcentration: string;
  activeContractors: readonly ContractorRow[];
  filteredContractors: readonly ContractorRow[];
  sorted: readonly ContractorRow[];
  sortKey: string | null;
  sortDirection: 'asc' | 'desc' | null;
  onSort: (key: string) => void;
  search: string;
  setSearch: (v: string) => void;
  navigate: (hash: string) => void;
  avgGpPerHour: string | null;
}) {
  return (
    <>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card delay={280}>
          <CardHeader title="Gross profit by customer" subtitle="Click a customer to drill down" />
          <BarListSummary
            viz={
              <MiniDonut
                size={40}
                segments={[
                  {
                    value: top5Clients.reduce((s, c) => s + Number(c.grossProfit), 0),
                    color: '#16a34a',
                    label: 'Top 5',
                  },
                  {
                    value: Math.max(
                      Number(summary.grossProfit) -
                        top5Clients.reduce((s, c) => s + Number(c.grossProfit), 0),
                      0,
                    ),
                    color: '#d4d4d4',
                    label: 'Other',
                  },
                ]}
              />
            }
          >
            <span className="figure font-semibold">{moneyCompact(summary.grossProfit)}</span>
            <span>
              {clients.length} customer{clients.length !== 1 ? 's' : ''}
            </span>
            <span>Top 5 = {topConcentration}</span>
          </BarListSummary>
          <div className="max-h-[400px] overflow-y-auto">
            <BarList
              data={clients.slice(0, 20).map((c) => {
                const m =
                  Number(c.billed) > 0 ? (Number(c.grossProfit) / Number(c.billed)) * 100 : 0;
                const concentration =
                  Number(summary.grossProfit) > 0
                    ? (Number(c.grossProfit) / Number(summary.grossProfit)) * 100
                    : 0;
                return {
                  label: c.name,
                  value: c.grossProfit,
                  note: `${c.contractors} contractors · ${hours(c.hours)} h · ${marginPercent(c.grossProfit, c.billed)} margin`,
                  badge: {
                    text: `${m.toFixed(1)}%`,
                    tone:
                      m >= 25
                        ? ('profit' as const)
                        : m >= 10
                          ? ('caution' as const)
                          : ('danger' as const),
                  },
                  flagged: concentration > 15,
                };
              })}
              limit={20}
              onClick={(index) => {
                const client = clients[index];
                if (client !== undefined) navigate(`client/${client.id}`);
              }}
            />
          </div>
        </Card>

        <Card delay={300}>
          <CardHeader
            title="Client concentration analysis"
            subtitle="Revenue risk from over-reliance on top accounts"
          />
          <div className="px-5 py-4 space-y-3">
            {clients.slice(0, 10).map((c) => {
              const pct =
                Number(summary.grossProfit) > 0
                  ? (Number(c.grossProfit) / Number(summary.grossProfit)) * 100
                  : 0;
              return (
                <div key={c.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-ink truncate max-w-[200px]">{c.name}</span>
                    <span
                      className={`figure font-medium ${pct > 15 ? 'text-caution' : 'text-ink-muted'}`}
                    >
                      {pct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-surface-overlay overflow-hidden">
                    <div
                      className={`h-full rounded-full ${pct > 15 ? 'bg-caution' : 'bg-profit'}`}
                      style={{
                        width: `${Math.min(pct, 100)}%`,
                        transition: 'width 600ms ease-out',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <div className="mt-4">
        <Card delay={360}>
          <CardHeader
            title="Contractors by gross profit"
            subtitle="Click a contractor to see their full history"
            aside={
              <div className="flex items-center gap-3">
                <div className="w-48">
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Filter contractors…"
                  />
                </div>
                <ExportButton
                  data={sorted.map((r) => ({
                    name: r.name,
                    client: r.clientName,
                    type: r.workerType === 'contractor_1099' ? '1099' : 'W2',
                    hours: hours(r.hours),
                    gpPerHour: rate(r.grossProfitPerHour),
                    grossProfit: money(r.grossProfit),
                    margin: marginPercent(r.grossProfit, r.billed),
                  }))}
                  filename="executive-contractors"
                  headers={[
                    { key: 'name', label: 'Contractor' },
                    { key: 'client', label: 'Customer' },
                    { key: 'type', label: 'Type' },
                    { key: 'hours', label: 'Hours' },
                    { key: 'gpPerHour', label: 'GP/h' },
                    { key: 'grossProfit', label: 'Gross Profit' },
                    { key: 'margin', label: 'Margin' },
                  ]}
                />
              </div>
            }
          />
          <BarListSummary
            vizGrow
            viz={
              <StackedBar
                segments={[
                  {
                    value: activeContractors.filter((c) => c.workerType !== 'contractor_1099')
                      .length,
                    color: '#16a34a',
                    label: 'W2',
                  },
                  {
                    value: activeContractors.filter((c) => c.workerType === 'contractor_1099')
                      .length,
                    color: '#2563eb',
                    label: '1099',
                  },
                ]}
              />
            }
          >
            <span>
              <span className="figure font-semibold">
                {moneyCompact(
                  activeContractors.reduce((s, c) => s + Number(c.grossProfit), 0).toFixed(4),
                )}
              </span>{' '}
              GP
            </span>
            <span>
              {avgGpPerHour !== null && (
                <>
                  <span className="figure font-semibold">{avgGpPerHour}</span>/h avg
                </>
              )}
            </span>
            <span>
              {activeContractors.length} contractor{activeContractors.length !== 1 ? 's' : ''}
            </span>
          </BarListSummary>
          <Table
            head={[
              { label: 'Contractor', sortKey: 'name' },
              { label: 'Customer', sortKey: 'client' },
              { label: 'Type', sortKey: 'type' },
              { label: 'Hours', align: 'right', sortKey: 'hours' },
              { label: 'GP/h', align: 'right', sortKey: 'gpPerHour' },
              { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
              { label: 'Margin', align: 'right', sortKey: 'margin' },
            ]}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={onSort}
          >
            {sorted.slice(0, 25).map((row) => (
              <Row key={row.id} onClick={() => navigate(`contractor/${row.id}`)}>
                <Cell>
                  <span className="text-ink cursor-pointer hover:text-profit transition-colors">
                    {row.name}
                  </span>
                </Cell>
                <Cell>{row.clientName}</Cell>
                <Cell muted>{row.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
                <Cell align="right" mono>
                  {hours(row.hours)}
                </Cell>
                <Cell align="right" mono>
                  {rate(row.grossProfitPerHour)}
                </Cell>
                <Cell align="right" mono>
                  <span className="text-profit">{money(row.grossProfit)}</span>
                </Cell>
                <Cell align="right" mono muted>
                  {marginPercent(row.grossProfit, row.billed)}
                </Cell>
              </Row>
            ))}
          </Table>
        </Card>
      </div>
    </>
  );
}

/* ── Commission Tab ── */

interface RecruiterCommission {
  name: string;
  id: string;
  grossProfit: string;
  hours: string;
  contractors: number;
  commission: string;
}

function CommissionTab({
  commissionByRecruiter,
  totalCommission,
  tiers,
  navigate,
}: {
  commissionByRecruiter: readonly RecruiterCommission[];
  totalCommission: string;
  tiers: readonly TierInfo[];
  navigate: (hash: string) => void;
}) {
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Card delay={280}>
        <CardHeader
          title="Commission cost summary"
          subtitle="Estimated marginal commission by recruiter"
        />
        <Table
          head={[
            { label: 'Recruiter' },
            { label: 'GP generated', align: 'right' },
            { label: 'Contractors', align: 'right' },
            { label: 'Commission', align: 'right' },
            { label: 'Eff. Rate', align: 'right' },
          ]}
        >
          {commissionByRecruiter.map((r) => {
            const effRate =
              Number(r.grossProfit) > 0
                ? ((Number(r.commission) / Number(r.grossProfit)) * 100).toFixed(1)
                : '0.0';
            return (
              <Row key={r.name} onClick={() => navigate(`recruiter/${String(r.id)}`)}>
                <Cell>
                  <span className="text-ink cursor-pointer hover:text-profit transition-colors">
                    {r.name}
                  </span>
                </Cell>
                <Cell align="right" mono>
                  <span className="text-profit">{money(r.grossProfit)}</span>
                </Cell>
                <Cell align="right" mono muted>
                  {r.contractors}
                </Cell>
                <Cell align="right" mono>
                  {money(r.commission)}
                </Cell>
                <Cell align="right" mono muted>
                  {effRate}%
                </Cell>
              </Row>
            );
          })}
        </Table>
        <div className="border-t border-edge px-5 py-3 flex items-center justify-between">
          <span className="text-xs font-medium text-ink-muted">Total commission cost</span>
          <span className="figure text-sm font-semibold text-ink">{money(totalCommission)}</span>
        </div>
      </Card>

      <Card delay={300}>
        <CardHeader title="Commission tier structure" subtitle="Current tier brackets and rates" />
        <Table
          head={[
            { label: 'Tier' },
            { label: 'GP Threshold', align: 'right' },
            { label: 'Rate', align: 'right' },
          ]}
        >
          {tiers.map((t, i) => (
            <Row key={t.threshold}>
              <Cell>Tier {i + 1}</Cell>
              <Cell align="right" mono>
                {money(t.threshold)}
              </Cell>
              <Cell align="right" mono>
                {(Number(t.rate) * 100).toFixed(0)}%
              </Cell>
            </Row>
          ))}
        </Table>
        <div className="border-t border-edge px-5 py-4">
          <p className="text-xs text-ink-faint">
            Commission is calculated marginally — each dollar of GP earns the rate of the tier it
            falls within, not a flat rate on the whole amount.
          </p>
        </div>
      </Card>
    </div>
  );
}

function BridgeRow({
  label,
  value,
  tone,
  indent,
  bold,
}: {
  label: string;
  value: string;
  tone: 'revenue' | 'profit' | 'muted';
  indent?: boolean;
  bold?: boolean;
}) {
  const colorClass =
    tone === 'revenue' ? 'text-revenue' : tone === 'profit' ? 'text-profit' : 'text-ink-muted';
  return (
    <div className={`flex items-baseline justify-between gap-2 ${indent ? 'pl-4' : ''}`}>
      <span className={`text-sm ${bold ? 'font-semibold text-ink' : 'text-ink-muted'}`}>
        {label}
      </span>
      <span className={`figure text-sm font-medium ${colorClass}`}>{value}</span>
    </div>
  );
}

function MixStat({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-ink-muted">{label}</span>
        <span className="figure text-sm text-ink">
          {count} <span className="text-ink-faint">({pct}%)</span>
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-overlay">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${Math.max(Number(pct), 2)}%`, transition: 'width 600ms ease-out' }}
        />
      </div>
    </div>
  );
}
